<template>
    <EditingDialog 
        :context="context"
        :showSnackbar="true"
        maxDialogWidth="1500"
        maxCardHeight="800"
        snackbarTextLangConst="lang_GENERAL_SNACKBAR_TEXT_CLICK_MAIN_SAVE_BUTTON"
        :useDifferentShowDialogProp="true"
    >
    <template v-slot:editForm>
        <v-row>
            <!-- Column 1 -->
            <v-col>
                <v-row>
                    <BaseEditPageTextField 
                        :context="context"
                        field="CompanyName"
                        :label="$t('lang_GENERIC_ACCOUNT_COMPANIES_CAPTION_COMPANY_NAME')"
                        rules="required"
                        :alvqa="alvqa +'-textfield-company-name'"
                    />
                </v-row>
                <v-row>
                    <BaseEditPageTextField 
                        :context="context"
                        field="AddressRow1"
                        :label="$t('lang_AZ_PERSONS_CAPTION_ADDRESS_ROW_1')"
                        rules="required"
                        :alvqa="alvqa +'-textfield-address-row-1'"
                    />
                </v-row>
                <v-row>
                    <BaseEditPageTextField 
                        :context="context"
                        field="AddressRow2"
                        :label="$t('lang_AZ_PERSONS_CAPTION_ADDRESS_ROW_2')"
                        :alvqa="alvqa +'-textfield-address-row-2'"
                    />
                </v-row>
                <v-row>
                    <BaseEditPageTextField 
                        :context="context"
                        field="Zip"
                        :label="$t('lang_AZ_PERSONS_CAPTION_ZIP')"
                        rules="required"
                        :alvqa="alvqa +'-textfield-zip'"
                    />
                </v-row>
                <v-row>
                    <BaseEditPageTextField 
                        :context="context"
                        field="City"
                        :label="$t('lang_AZ_PERSONS_CAPTION_CITY')"
                        rules="required"
                        :alvqa="alvqa +'-textfield-city'"
                    />
                </v-row>
                <v-row>
                    <BaseEditPageTextField 
                        :context="context"
                        field="State"
                        :label="$t('lang_AZ_PERSONS_CAPTION_STATE')"
                        :alvqa="alvqa + '-textfield-state'"
                    />
                </v-row> 
                <v-row>
                    <BaseEditPageDropdown 
                        :context="context"
                        field="Country"
                        rules="required"
                        :items="dropdownCountries"
                        :label="$t('lang_AZ_PERSONS_CAPTION_COUNTRY')"
                        :alvqa="alvqa +'-textfield-country'"
                    />
                </v-row>
                <v-row>
                    <BaseEditPageTextarea 
                        :context="context" 
                        field="StatementText"
                        rows="3"
                        :label="$t('lang_GENERIC_ACCOUNT_COMPANIES_CAPTION_STATEMENT_TEXT')" 
                        :alvqa="alvqa + '-textarea-statement-text'"
                    />
                </v-row>
            </v-col>
        
            <!-- Column 2 -->
            <v-col>
                <v-row>
                    <BaseEditPageTextField 
                        :context="context"
                        field="SenderRow"
                        :label="$t('lang_GENERIC_ACCOUNT_COMPANIES_CAPTION_SENDER_ROW')"
                        rules="required"
                        :alvqa="alvqa +'-textfield-sender-row'"
                    />
                </v-row>
                <v-row>
                    <BaseEditPageTextField 
                        :context="context"
                        field="GemaLicenseeNumber"
                        :label="$t('lang_ACCOUNT_COMPANIES_LABEL_COMPANIES_GEMA_LICENSEE_NO')"
                        rules="numeric"
                        :alvqa="alvqa +'-textfield-gema-licensee-number'"
                    />
                </v-row>
                <v-row>
                    <v-col class="pt-0 pl-0 pb-0">
                        <BaseEditPageTextField 
                            :context="context"
                            field="GemaTasAudio"
                            :label="$t('lang_ACCOUNT_COMPANIES_LABEL_COMPANIES_GEMA_TAS_AUDIO')"
                            :alvqa="alvqa +'-textfield-gema-tas-audio'"
                        />
                    </v-col>
                    <v-col class="pt-0 pl-0 pb-0">
                        <BaseEditPageTextField 
                            :context="context"
                            field="GemaTasVideo"
                            :label="$t('lang_ACCOUNT_COMPANIES_LABEL_COMPANIES_GEMA_TAS_VIDEO')"
                            :alvqa="alvqa +'-textfield-gema-tas-video'"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <BaseEditPageTextField 
                        :context="context"
                        field="VatId"
                        :label="$t('lang_GENERIC_CAPTION_VAT_ID')"
                        :alvqa="alvqa +'-textfield-vat-id'"
                    />
                </v-row>
                <v-row>
                    <BaseEditPageTextField
                        :context="context"
                        field="CustomsId"
                        :label="$t('lang_GENERIC_CAPTION_CUSTOMS_ID')"
                        :alvqa="alvqa +'-textfield-customs-id'"
                    />
                </v-row>
                <v-row>
                    <BaseEditPageTextarea 
                        :context="context" 
                        field="FooterLeft"
                        rows="5"
                        :label="$t('lang_GENERIC_ACCOUNT_COMPANIES_CAPTION_FOOTER_LEFT')"
                        :alvqa="alvqa + '-textarea-footer-left'"
                    />
                </v-row>
                <div class="footer-char-counter" :class="{ 'footer-char-counter--exceeded': footerExceedsLimit }">
                    {{ footerTotalLength }} / 180<span v-if="footerExceedsLimit"> — {{ $t('lang_GENERIC_MESSAGE_WARNING_EXCEL_FOOTER_CHAR_LIMIT') }}</span>
                </div>
            </v-col>

            <!-- Column 3 -->
            <v-col>
                <v-row>
                    <BaseEditPageTextField 
                        :context="context"
                        field="InvoicesNumberRange"
                        :label="$t('lang_GENERIC_ACCOUNT_COMPANIES_CAPTION_INVOICES_NO_RANGE')"
                        :alvqa="alvqa +'-textfield-invoices-number-range'"
                    />
                </v-row>
                <v-row>
                    <BaseEditPageTextField 
                        :context="context"
                        field="ShippingNumberRange"
                        :label="$t('lang_GENERIC_ACCOUNT_COMPANIES_CAPTION_SHIPPING_NO_RANGE')"
                        :alvqa="alvqa +'-textfield-shipping-number-range'"
                    />
                </v-row>
                <v-row>
                    <BaseEditPageTextField 
                        :context="context"
                        field="CreditNumberRange"
                        :label="$t('lang_GENERIC_ACCOUNT_COMPANIES_CAPTION_CREDIT_NO_RANGE')"
                        :alvqa="alvqa +'-textfield-credit-number-range'"
                    />
                </v-row>
                <v-row>
                    <BaseEditPageTextField 
                        :context="context"
                        field="StatementNumberRange"
                        :label="$t('lang_GENERIC_ACCOUNT_COMPANIES_CAPTION_STATEMENT_NO_RANGE')"
                        :alvqa="alvqa +'-textfield-statement-number-range'"
                    />
                </v-row>
                <v-row class="textfield_placeholder">
                </v-row>
                <v-row>
                    <BaseEditPageTextarea 
                        :context="context" 
                        field="FooterMiddle"
                        rows="5"
                        :label="$t('lang_GENERIC_ACCOUNT_COMPANIES_CAPTION_FOOTER_MIDDLE')" 
                        :alvqa="alvqa + '-textarea-footer-middle'"
                    />
                </v-row>

            </v-col>

            <!-- Column 4 -->
            <v-col>
                <v-row>
                    <BaseEditPageTextField 
                        :context="context"
                        field="InvoicesNextValue"
                        :label="$t('lang_GENERIC_ACCOUNT_COMPANIES_CAPTION_NEXT_VALUE')"
                        :alvqa="alvqa +'-textfield-invoices-next-value'"
                    />
                </v-row>
                <v-row>
                    <BaseEditPageTextField 
                        :context="context"
                        field="ShippingNextValue"
                        :label="$t('lang_GENERIC_ACCOUNT_COMPANIES_CAPTION_NEXT_VALUE')"
                        :alvqa="alvqa +'-textfield-shipping-next-value'"
                    />
                </v-row>
                <v-row>
                    <BaseEditPageTextField 
                        :context="context"
                        field="CreditNextValue"
                        :label="$t('lang_GENERIC_ACCOUNT_COMPANIES_CAPTION_NEXT_VALUE')"
                        :alvqa="alvqa +'-textfield-credit-next-value'"
                    />
                </v-row>
                <v-row>
                    <BaseEditPageTextField 
                        :context="context"
                        field="StatementNextValue"
                        :label="$t('lang_GENERIC_ACCOUNT_COMPANIES_CAPTION_NEXT_VALUE')"
                        :alvqa="alvqa +'-textfield-statement-next-value'"
                    />
                </v-row>
                <v-row class="textfield_placeholder">
                </v-row>
                <v-row>
                     <BaseEditPageTextarea 
                        :context="context" 
                        field="FooterRight"
                        rows="5"
                        :label="$t('lang_GENERIC_ACCOUNT_COMPANIES_CAPTION_FOOTER_RIGHT')" 
                        :alvqa="alvqa + '-textarea-footer-right'"
                    />
                </v-row>
            </v-col>

        </v-row>

    </template>
    </EditingDialog>
</template>

<script>
import EditingDialog from '@/components/_dialogs/EditingDialog'
import BaseRegions from '@/components/_base/BaseRegions'
import BaseEditPageTextField from '@/components/_editForm/BaseEditPageTextField'
import BaseEditPageDropdown from '@/components/_editForm/BaseEditPageDropdown'
import BaseEditPageTextarea from '@/components/_editForm/BaseEditPageTextarea'

export default {
    name: "AccountLabelCompaniesEditPanelDialog",
    components: {
        EditingDialog,
        BaseRegions,
        BaseEditPageTextField,
        BaseEditPageDropdown,
        BaseEditPageTextarea
    },
    data() {
        return {
            context: "AccountLabelCompanies",
            alvqa: "account-label-companies-panel-editing-dialog"
        }
    },
    computed: {
        dropdownCountries() { return this.$alvPlugin.getListOfTranslatedCountries() },
        footerTotalLength() {
            const left = this.$store.getters['get' + this.context + 'DetailsFooterLeft'] || ''
            const middle = this.$store.getters['get' + this.context + 'DetailsFooterMiddle'] || ''
            const right = this.$store.getters['get' + this.context + 'DetailsFooterRight'] || ''
            return left.length + middle.length + right.length
        },
        footerExceedsLimit() {
            return this.footerTotalLength > 180
        },
    },
    created() {
        this.$store.commit('setCountries')
    },
}
</script>

<style scoped>
.textfield_placeholder {
    min-height: 70px;
}
.footer-char-counter {
    font-size: 12px;
    margin-top: -18px;
    padding-left: 4px;
    color: #9e9e9e;
}
.footer-char-counter--exceeded {
    color: #1565C0;
}
</style>